import React, { useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import hygeaLogo from '../../assets/hygea.png';

import { API_BASE } from '../../config/runtime';

const normalizeStatus = (status) => {
  if (!status) return '';
  const s = status.trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const getStatusColor = (status) => {
  switch (normalizeStatus(status)) {
    case 'Completed': return 'bg-blue-100 text-blue-700';
    case 'Approved':  return 'bg-emerald-100 text-emerald-700';
    case 'Pending':   return 'bg-amber-100 text-amber-700';
    case 'Declined':  return 'bg-red-100 text-red-700';
    case 'Cancelled': return 'bg-gray-100 text-gray-600';
    default:          return 'bg-gray-100 text-gray-600';
  }
};

const AdminDashboard = () => {
  const [activePeriod, setActivePeriod] = useState('monthly');
  const [expandedBranch, setExpandedBranch] = useState(null);
  const [branchEarnings, setBranchEarnings] = useState([]);
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  const periods = [
    { key: 'daily',   label: 'Daily'   },
    { key: 'monthly', label: 'Monthly' },
    { key: 'yearly',  label: 'Yearly'  },
  ];
  const activePeriodLabel = periods.find((p) => p.key === activePeriod)?.label || 'Monthly';

  useEffect(() => {
    const fetchDashboard = async () => {
      setDashboardLoading(true);
      setDashboardError('');
      try {
        const response = await fetch(`${API_BASE}/get_admin_dashboard.php`);
        const data = await response.json();
        if (data.success) {
          setBranchEarnings(data.branches || []);
          setStats(data.stats || null);
          setRecentBookings(data.recent_bookings || []);
        } else {
          setDashboardError(data.message || 'Unable to load dashboard data.');
        }
      } catch (error) {
        console.error(error);
        setDashboardError('Unable to load dashboard data.');
      } finally {
        setDashboardLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const totalEarnings = useMemo(() => {
    if (activePeriod === 'monthly' && selectedMonth) {
      // Filter by selected month when monthly view is active
      return branchEarnings.reduce((sum, item) => {
        const monthlyData = item.monthly_breakdown ? item.monthly_breakdown[selectedMonth] : 0;
        return sum + Number(monthlyData || 0);
      }, 0);
    }
    return branchEarnings.reduce((sum, item) => sum + Number(item[activePeriod] || 0), 0);
  }, [branchEarnings, activePeriod, selectedMonth]);

  const highestEarning = useMemo(() => {
    if (branchEarnings.length === 0) return 0;
    if (activePeriod === 'monthly' && selectedMonth) {
      return Math.max(...branchEarnings.map((item) => {
        const monthlyData = item.monthly_breakdown ? item.monthly_breakdown[selectedMonth] : 0;
        return Number(monthlyData || 0);
      }));
    }
    return Math.max(...branchEarnings.map((item) => Number(item[activePeriod] || 0)));
  }, [branchEarnings, activePeriod, selectedMonth]);

  const statCards = stats ? [
    { label: 'Total Bookings', value: stats.total_bookings,                                               color: 'text-gray-800',    bg: 'bg-gray-50',    border: 'border-gray-200' },
    { label: 'Pending',        value: stats.pending,                                                      color: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-200' },
    { label: 'Approved',       value: stats.approved,                                                     color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    { label: 'Completed',      value: stats.completed,                                                    color: 'text-blue-600',    bg: 'bg-blue-50',    border: 'border-blue-200' },
    { label: 'Cancelled',      value: stats.cancelled,                                                    color: 'text-gray-500',    bg: 'bg-gray-50',    border: 'border-gray-200' },
    { label: 'Total Revenue',  value: `₱${Number(stats.total_revenue || 0).toLocaleString()}`,            color: 'text-[#8b5b42]',   bg: 'bg-[#fff8f4]',  border: 'border-[#f3e6dd]' },
  ] : [];

  return (
    <div className="flex flex-col sm:flex-row min-h-screen hygea-app-bg">
      <AdminSidebar />

      <div className="flex-1 flex flex-col min-w-0">

        {/* Top bar */}
        <div className="relative z-10 bg-white shadow-sm px-4 sm:px-8 py-3 sm:py-4 flex justify-between items-center">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Dashboard</h1>
          <img src={hygeaLogo} alt="Hygea Wellness Spa" className="h-9 sm:h-12" />
        </div>

        <div className="flex-1 p-4 sm:p-8 overflow-y-auto">
          <div className="space-y-5 sm:space-y-6">

            {/* Welcome banner */}
            <div className="rounded-2xl sm:rounded-3xl border border-[#e8d9cf] bg-white p-4 sm:p-8 shadow-[0_14px_35px_rgba(0,0,0,0.08)]">
              <div className="rounded-xl sm:rounded-2xl border border-[#eadfd8] bg-gradient-to-r from-[#fff6f1] to-[#fffdfb] px-4 sm:px-6 py-4 sm:py-6">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#b07b60]">Hygea Control Center</p>
                <h2 className="mt-1 text-xl sm:text-3xl font-bold text-gray-800">Welcome to Admin Dashboard</h2>
                <p className="mt-1 sm:mt-2 text-sm text-gray-600 hidden sm:block">Monitor performance and review branch highlights from the dashboard overview.</p>
              </div>

              {/* Stats Cards — 2 cols mobile, 3 cols md, 6 cols lg */}
              <div className="mt-4 sm:mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                {dashboardLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="rounded-xl sm:rounded-2xl border border-gray-100 bg-gray-50 p-3 sm:p-4 animate-pulse">
                      <div className="h-2.5 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  ))
                ) : (
                  statCards.map((card) => (
                    <div key={card.label} className={`rounded-xl sm:rounded-2xl border ${card.border} ${card.bg} p-3 sm:p-4`}>
                      <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.15em] sm:tracking-[0.2em] text-gray-400 leading-tight">{card.label}</p>
                      <p className={`mt-1 text-xl sm:text-2xl font-bold ${card.color}`}>{card.value}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Earnings + Most Purchased — stacked on mobile, side-by-side on lg */}
            <div className="grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">

              {/* Branch Earnings */}
              <div className="rounded-2xl border border-[#eadfd8] bg-white p-4 sm:p-6 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b07b60]">Branch Earnings</p>
                    <h3 className="mt-1 text-lg sm:text-2xl font-bold text-gray-800">Earnings by Branch</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {activePeriodLabel} earnings from completed bookings per branch.
                    </p>
                    <div className="mt-3 sm:mt-4 flex flex-wrap gap-2">
                      {periods.map((period) => (
                        <button
                          key={period.key}
                          type="button"
                          onClick={() => setActivePeriod(period.key)}
                          className={`rounded-full px-3 sm:px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] transition ${
                            activePeriod === period.key
                              ? 'bg-[#b07b60] text-white shadow-sm'
                              : 'bg-[#f6ebe4] text-[#8b5b42] hover:bg-[#ecdcd1]'
                          }`}
                        >
                          {period.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Total earnings badge + Month selector */}
                  <div className="flex flex-col gap-2 items-end self-start">
                    <div className="rounded-xl sm:rounded-2xl border border-[#f3e6dd] bg-[#fff8f4] px-4 sm:px-5 py-3 text-right w-full sm:w-auto">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#b07b60]">
                        Total {activePeriodLabel}
                      </p>
                      <p className="mt-1 text-xl sm:text-2xl font-bold text-[#8b5b42]">
                        {dashboardLoading ? '—' : `₱${totalEarnings.toLocaleString()}`}
                      </p>
                    </div>
                    {activePeriod === 'monthly' && (
                      <input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="rounded-lg px-3 sm:px-4 py-2 text-xs font-semibold border border-[#e8d9cf] bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#b07b60]"
                      />
                    )}
                  </div>
                </div>

                <div className="mt-4 sm:mt-5 space-y-3 sm:space-y-4">
                  {dashboardError && <p className="text-sm text-red-500">{dashboardError}</p>}
                  {dashboardLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="rounded-xl sm:rounded-2xl border border-gray-100 bg-gray-50 p-3 sm:p-4 animate-pulse">
                        <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                        <div className="h-2 bg-gray-100 rounded-full w-full mt-3"></div>
                      </div>
                    ))
                  ) : branchEarnings.length === 0 ? (
                    <p className="text-sm text-gray-500">No earnings data available yet.</p>
                  ) : (
                    branchEarnings.map((item) => {
                      let amount = 0;
                      if (activePeriod === 'monthly' && selectedMonth) {
                        amount = Number(item.monthly_breakdown && item.monthly_breakdown[selectedMonth] || 0);
                      } else {
                        amount = Number(item[activePeriod] || 0);
                      }
                      const pct = highestEarning ? Math.round((amount / highestEarning) * 100) : 0;
                      return (
                        <div key={item.branch_id} className="rounded-xl sm:rounded-2xl border border-[#f2e5dc] bg-[#fffdfb] px-3 sm:px-4 py-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-gray-800">{item.branch_name}</p>
                              <p className="text-xs text-gray-400">
                                {activePeriod === 'monthly' && selectedMonth ? 'Monthly bookings' : `${item.completed_bookings} completed booking${item.completed_bookings !== 1 ? 's' : ''}`}
                              </p>
                            </div>
                            <p className="text-sm font-bold text-[#8b5b42]">₱{amount.toLocaleString()}</p>
                          </div>
                          <div className="mt-2 sm:mt-3 h-2 w-full rounded-full bg-[#f6ebe4]">
                            <div
                              className="h-2 rounded-full bg-gradient-to-r from-[#b07b60] to-[#7f563f] transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Most Purchased Services */}
              <div className="rounded-2xl border border-[#eadfd8] bg-white p-4 sm:p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b07b60]">Branch Services</p>
                <h3 className="mt-1 text-lg sm:text-2xl font-bold text-gray-800">Most Purchased</h3>
                <p className="mt-1 text-sm text-gray-500">Top services by completed bookings per branch.</p>

                <div className="mt-4 sm:mt-5 divide-y divide-[#f2e5dc] rounded-xl sm:rounded-2xl border border-[#f2e5dc] bg-[#fffdfb]">
                  {dashboardLoading ? (
                    <p className="px-4 py-3 text-sm text-gray-500 animate-pulse">Loading services...</p>
                  ) : branchEarnings.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-gray-500">No service data available yet.</p>
                  ) : (
                    branchEarnings.map((item, index) => {
                      const topServices = Array.isArray(item.mostPurchased) ? item.mostPurchased : [];
                      const isExpanded = expandedBranch === item.branch_id;
                      return (
                        <div key={item.branch_id} className="px-3 sm:px-4 py-3">
                          <div className="flex items-center justify-between gap-2 sm:gap-3">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                              <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg sm:rounded-xl bg-[#f6ebe4] text-xs sm:text-sm font-bold text-[#8b5b42] flex-shrink-0">
                                {String(index + 1).padStart(2, '0')}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-800 truncate">{item.branch_name}</p>
                                <p className="text-xs text-gray-500">{topServices.length} top service{topServices.length !== 1 ? 's' : ''}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setExpandedBranch(isExpanded ? null : item.branch_id)}
                              disabled={topServices.length === 0}
                              className="flex-shrink-0 rounded-full border border-[#f2e5dc] px-2.5 sm:px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8b5b42] transition hover:bg-[#f6ebe4] disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {isExpanded ? 'Hide' : 'View'}
                            </button>
                          </div>
                          {isExpanded && topServices.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {topServices.map((svc, i) => (
                                <div key={`${item.branch_id}-${svc.name}`} className="flex items-center justify-between rounded-xl bg-[#f6ebe4] px-3 py-2">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-[10px] font-black text-[#b07b60] flex-shrink-0">#{i + 1}</span>
                                    <span className="text-xs font-semibold text-[#5c3d2e] uppercase tracking-wide truncate">{svc.name}</span>
                                  </div>
                                  <span className="text-[10px] font-bold text-[#8b5b42] flex-shrink-0 ml-2">{svc.count}×</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {isExpanded && topServices.length === 0 && (
                            <p className="mt-2 text-xs text-gray-400 italic px-1">No completed bookings for this branch yet.</p>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Recent Bookings */}
            <div className="rounded-2xl border border-[#eadfd8] bg-white p-4 sm:p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b07b60]">Activity</p>
              <h3 className="mt-1 text-lg sm:text-xl font-bold text-gray-800">Recent Bookings</h3>
              <p className="mt-1 text-sm text-gray-500 mb-4 sm:mb-5">Latest 5 bookings across all branches.</p>

              {dashboardLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-12 sm:h-14 rounded-xl bg-gray-50 animate-pulse" />
                  ))}
                </div>
              ) : recentBookings.length === 0 ? (
                <p className="text-sm text-gray-500">No bookings yet.</p>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead>
                        <tr className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 border-b border-gray-100">
                          <th className="pb-3">Booking ID</th>
                          <th className="pb-3">Client</th>
                          <th className="pb-3">Branch</th>
                          <th className="pb-3">Date</th>
                          <th className="pb-3">Amount</th>
                          <th className="pb-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {recentBookings.map((booking) => (
                          <tr key={booking.booking_id} className="hover:bg-[#fffaf7] transition-colors">
                            <td className="py-3 font-mono text-xs text-gray-500">#{String(booking.booking_id).padStart(5, '0')}</td>
                            <td className="py-3 font-semibold text-gray-800">{booking.client_name}</td>
                            <td className="py-3 text-gray-600">{booking.branch_name || '—'}</td>
                            <td className="py-3 text-gray-500">{booking.appointment_date || '—'}</td>
                            <td className="py-3 font-bold text-[#8b5b42]">₱{Number(booking.total_price || 0).toLocaleString()}</td>
                            <td className="py-3">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${getStatusColor(booking.status)}`}>
                                {normalizeStatus(booking.status)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="sm:hidden space-y-3">
                    {recentBookings.map((booking) => (
                      <div key={booking.booking_id} className="rounded-xl border border-[#f2e5dc] bg-[#fffdfb] p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-xs text-gray-400">#{String(booking.booking_id).padStart(5, '0')}</span>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${getStatusColor(booking.status)}`}>
                            {normalizeStatus(booking.status)}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-gray-800">{booking.client_name}</p>
                        <div className="flex items-center justify-between mt-1.5">
                          <div>
                            <p className="text-xs text-gray-500">{booking.branch_name || '—'}</p>
                            <p className="text-xs text-gray-400">{booking.appointment_date || '—'}</p>
                          </div>
                          <p className="text-sm font-bold text-[#8b5b42]">₱{Number(booking.total_price || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
